import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';

/** `POST /auth/driver/send-code` — answers 204 whether or not the number belongs to a driver. */
export const sendDriverCodeSchema = z.object({ phone: kePhoneSchema }).strict();
export type SendDriverCodeInput = z.infer<typeof sendDriverCodeSchema>;

/** `POST /auth/driver/verify-code`. `deviceId` is generated by the app once per install and stays the same across refreshes. */
export const verifyDriverCodeSchema = z
  .object({
    phone: kePhoneSchema,
    code: z.string().regex(/^\d{6}$/, 'Expected the 6-digit code from the SMS'),
    deviceId: z.string().trim().min(1).max(128),
  })
  .strict();
export type VerifyDriverCodeInput = z.infer<typeof verifyDriverCodeSchema>;

/** `POST /auth/driver/refresh` — the refresh token is in the body, not a bearer header, since it must work once the device token has expired. */
export const refreshDriverTokenSchema = z.object({ refreshToken: z.string().min(1).max(256) }).strict();
export type RefreshDriverTokenInput = z.infer<typeof refreshDriverTokenSchema>;

/** Both are opaque; expiry is an ISO instant. The refresh token is replaced on every refresh, so the app stores the newest pair. */
export interface DriverAuthTokens {
  deviceToken: string;
  deviceTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

/** The `reason` on a 401 from verify-code. A number that isn't an active driver's reads as `wrong_code`, never anything more telling. */
export const DRIVER_LOGIN_FAILURE_REASONS = ['wrong_code', 'expired', 'exhausted'] as const;
export type DriverLoginFailureReason = (typeof DRIVER_LOGIN_FAILURE_REASONS)[number];

/** The `reason` on a 429 from send-code; `retryAfterSeconds` accompanies `too_soon`. */
export const DRIVER_CODE_SEND_REFUSALS = ['too_soon', 'limit_reached'] as const;
export type DriverCodeSendRefusal = (typeof DRIVER_CODE_SEND_REFUSALS)[number];

/** `GET /auth/driver/me`. */
export interface DriverSelf {
  id: string;
  name: string;
  phone: string;
}
